// 학기 상세 페이지 - 근무자 관리
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  useToast,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Spinner
} from '@chakra-ui/react';
import { ArrowBackIcon, AddIcon, DeleteIcon, EditIcon, ViewIcon } from '@chakra-ui/icons';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import {
  getSemesterWorkers,
  removeSemesterWorker,
  updateWorkerMaxHours,
  getAcademicSchedules
} from 'utils/firestoreUtils';
import WorkerSelectionModal from './components/WorkerSelectionModal';

function SemesterDetail() {
  const { semesterId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');

  const { isOpen: isSelectionOpen, onOpen: onSelectionOpen, onClose: onSelectionClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isScheduleOpen, onOpen: onScheduleOpen, onClose: onScheduleClose } = useDisclosure();

  const [semester, setSemester] = useState(location.state?.semester || null);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [newMaxHours, setNewMaxHours] = useState(0);
  const [viewingSchedule, setViewingSchedule] = useState(null);

  useEffect(() => {
    if (semesterId) {
      loadWorkers();
    }
  }, [semesterId]);

  const loadWorkers = async () => {
    try {
      setLoading(true);
      const data = await getSemesterWorkers(semesterId);
      setWorkers(data);
    } catch (error) {
      console.error('근무자 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '근무자 목록을 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveWorker = async (workerId) => {
    if (!window.confirm('이 근무자를 제거하시겠습니까? 이미 신청한 스케줄은 유지됩니다.')) return;

    try {
      await removeSemesterWorker(workerId);
      await loadWorkers();
      toast({
        title: '제거 완료',
        description: '근무자가 제거되었습니다.',
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('제거 오류:', error);
      toast({
        title: '제거 실패',
        description: '근무자 제거에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    }
  };

  const handleEditMaxHours = (worker) => {
    setEditingWorker(worker);
    setNewMaxHours(worker.maxWorkHours);
    onEditOpen();
  };

  const handleSaveMaxHours = async () => {
    if (newMaxHours < 1) {
      toast({
        title: '입력 오류',
        description: '최대 근로시간은 1시간 이상이어야 합니다.',
        status: 'warning',
        duration: 3000
      });
      return;
    }

    try {
      await updateWorkerMaxHours(editingWorker.id, newMaxHours);
      await loadWorkers();
      onEditClose();
      toast({
        title: '수정 완료',
        description: '최대 근로시간이 수정되었습니다.',
        status: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('수정 오류:', error);
      toast({
        title: '수정 실패',
        description: '최대 근로시간 수정에 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    }
  };

  const handleViewSchedule = async (worker) => {
    try {
      setLoading(true);
      const schedules = await getAcademicSchedules(worker.userId);
      const activeSchedule = schedules.find(s => s.semesterId === semesterId);
      setViewingSchedule(activeSchedule);
      onScheduleOpen();
    } catch (error) {
      console.error('학업시간표 조회 오류:', error);
      toast({
        title: '조회 실패',
        description: '학업시간표를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWorkersSaved = () => {
    loadWorkers();
    onSelectionClose();
  };

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 헤더 영역 */}
      <Flex justify="space-between" align="center" mb={6}>
        <Flex align="center" gap={4}>
          <IconButton
            icon={<ArrowBackIcon />}
            onClick={() => history.push('/admin/semester-management')}
            aria-label="뒤로 가기"
            variant="ghost"
          />
          <Box>
            <Heading size="lg" color={textColor} mb={1}>
              {semester?.semesterName} - 근무자 관리
            </Heading>
            <Text fontSize="sm" color="gray.500">
              선정된 근무자: {workers.length}명
            </Text>
          </Box>
        </Flex>
        <Button
          colorScheme="blue"
          leftIcon={<AddIcon />}
          onClick={onSelectionOpen}
        >
          근무자 선정
        </Button>
      </Flex>

      {/* 학기 정보 카드 */}
      {semester && (
        <Card mb={6}>
          <CardBody py={3}>
            <Flex gap={4} fontSize="sm" flexWrap="wrap">
              <Text>
                <strong>학기:</strong> {semester.startDate} ~ {semester.endDate}
              </Text>
              {semester.workplaces && semester.workplaces.length > 0 && (
                <Text>
                  <strong>근무지:</strong> {semester.workplaces.join(', ')}
                </Text>
              )}
              {semester.defaultMaxWorkHours && (
                <Text>
                  <strong>기본 최대 근로시간:</strong> {semester.defaultMaxWorkHours}시간
                </Text>
              )}
            </Flex>
          </CardBody>
        </Card>
      )}

      {/* 메인 컨텐츠 */}
      <Card>
        <CardBody>
          {loading ? (
            <Flex justify="center" py={10}>
              <Spinner size="xl" />
            </Flex>
          ) : workers.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text color="gray.500">선정된 근무자가 없습니다.</Text>
              <Text fontSize="sm" color="gray.400" mt={2}>
                "근무자 선정" 버튼을 눌러 근무자를 추가하세요.
              </Text>
            </Box>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>이름</Th>
                  <Th>연락처</Th>
                  <Th>신청 중 근로시간</Th>
                  <Th>완료 근로시간</Th>
                  <Th>잔여 근로시간</Th>
                  <Th>최대 근로시간</Th>
                  <Th>작업</Th>
                </Tr>
              </Thead>
              <Tbody>
                {workers.map((worker) => (
                  <Tr key={worker.id}>
                    <Td fontWeight="bold">{worker.userName}</Td>
                    <Td fontSize="sm">{worker.userPhone || worker.userEmail}</Td>
                    <Td>
                      <Badge colorScheme="yellow">{worker.appliedHours}시간</Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme="green">{worker.completedHours}시간</Badge>
                    </Td>
                    <Td>
                      <Badge 
                        colorScheme={worker.remainingHours > 10 ? 'blue' : worker.remainingHours > 0 ? 'orange' : 'red'}
                      >
                        {worker.remainingHours}시간
                      </Badge>
                    </Td>
                    <Td fontSize="sm">{worker.maxWorkHours}시간</Td>
                    <Td>
                      <Flex gap={2}>
                        <IconButton
                          icon={<ViewIcon />}
                          size="sm"
                          colorScheme="purple"
                          onClick={() => handleViewSchedule(worker)}
                          aria-label="학업시간표 조회"
                          title="학업시간표 조회"
                        />
                        <IconButton
                          icon={<EditIcon />}
                          size="sm"
                          onClick={() => handleEditMaxHours(worker)}
                          aria-label="최대 근로시간 수정"
                          title="최대 근로시간 수정"
                        />
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          colorScheme="red"
                          onClick={() => handleRemoveWorker(worker.id)}
                          aria-label="제거"
                        />
                      </Flex>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* 근무자 선정 모달 */}
      <WorkerSelectionModal
        isOpen={isSelectionOpen}
        onClose={onSelectionClose}
        semesterId={semesterId}
        semester={semester}
        onSaved={handleWorkersSaved}
      />

      {/* 최대 근로시간 수정 모달 */}
      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>최대 근로시간 수정</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>{editingWorker?.userName}님의 최대 근로시간</FormLabel>
              <Input
                type="number"
                min="1"
                value={newMaxHours}
                onChange={(e) => setNewMaxHours(parseInt(e.target.value) || 0)}
              />
              <Text fontSize="xs" color="gray.500" mt={2}>
                현재 신청 중: {editingWorker?.appliedHours}시간 | 완료: {editingWorker?.completedHours}시간
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              취소
            </Button>
            <Button colorScheme="blue" onClick={handleSaveMaxHours}>
              저장
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 학업시간표 조회 모달 */}
      <Modal isOpen={isScheduleOpen} onClose={onScheduleClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>학업시간표</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingSchedule ? (
              <Box>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  {viewingSchedule.semesterName} 학업시간표
                </Text>
                {Object.keys(viewingSchedule.weeklySchedule || {}).map((day) => {
                  const daySchedule = viewingSchedule.weeklySchedule[day];
                  if (!daySchedule || daySchedule.length === 0) return null;
                  
                  const dayNames = {
                    monday: '월요일',
                    tuesday: '화요일',
                    wednesday: '수요일',
                    thursday: '목요일',
                    friday: '금요일',
                    saturday: '토요일',
                    sunday: '일요일'
                  };

                  return (
                    <Box key={day} mb={3} p={3} bg="gray.50" borderRadius="md">
                      <Text fontWeight="bold" mb={2}>{dayNames[day]}</Text>
                      {daySchedule.map((cls, idx) => (
                        <Text key={idx} fontSize="sm">
                          • {cls.start} - {cls.end}: {cls.subject}
                        </Text>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Text color="gray.500" textAlign="center" py={10}>
                등록된 학업시간표가 없습니다.
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onScheduleClose}>닫기</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default SemesterDetail;
